import { all, call, put, select, take } from "redux-saga/effects";
import {
    IConsents,
    subscriptionPreferencesAT,
} from "../../subscriptionPreferences/SubscriptionPreferencesModel";
import {
    umSpPageAuthSelector,
    umSpPageStateSelector,
} from "../../_shared/selectors";
import { postJsonSaga, putJsonSaga } from "../_services/apiCalls";
import { loadConsents } from "../loadConsents/loadConsentsSaga";
import { showInlineErrorsOnSubscriptionPreferencesAction } from "../../subscriptionPreferences/subscriptionPreferencesActions";
import {
    lockScreenSaga,
    unLockScreenSaga,
} from "../../_modules/screenLocker/screenLockerFeatures";

export function* saveConsentsSaga() {
    while (yield take(subscriptionPreferencesAT.saveData)) {
        yield call(saveConsents);
    }
}

function* wrapFetch2(fn, token, url, data, type, key = null) {
    const [success, result] = yield fn(token, url, data);
    const fetchKey = [type, key].filter(x => x).join("_");
    if (success) return [success, result, fetchKey];

    if (!result.server)
        return [
            success,
            { type: "exception", value: result.message },
            fetchKey,
        ];

    return result.server?.some && result.server.some(x => x.ExternalError)
        ? [success, { type: "externalError", value: result.server }, fetchKey]
        : [success, { type: "serverError", value: result.message }, fetchKey];
}

const wrapApiCalls = token => ({
    putUserSubscription: subscription =>
        wrapFetch2(
            putJsonSaga,
            token,
            `v1/Profile/object/__usersubscriptions/${subscription.id}`,
            subscription,
            "subscription",
            subscription.id
        ),
    postUserSubscription: (subscription, id) =>
        wrapFetch2(
            postJsonSaga,
            token,
            "v1/Profile/object/__usersubscriptions",
            subscription,
            "subscription",
            id
        ),
    postUserConsent: generalConsents =>
        wrapFetch2(
            postJsonSaga,
            token,
            "v1/Profile/object/__consent",
            generalConsents,
            "consent"
        ),
});

function* saveConsents() {
    const {
        data: { generalConsents, subscriptions },
        oldData,
    } = yield select(umSpPageStateSelector);
    const { token } = yield select(umSpPageAuthSelector);
    const lockUid = yield lockScreenSaga();

    const api = wrapApiCalls(token);
    const effects: any = Object.keys(subscriptions)
        .map(key => {
            const oldS = oldData.subscriptions[key];
            const newS = subscriptions[key];
            if (isEqual(oldS, newS)) return null;

            if (Number.isFinite(newS.id)) return api.putUserSubscription(newS);

            if (isEmpty(newS)) return null;

            const s = { ...newS };
            delete s.id;
            return api.postUserSubscription(s, newS.id);
        })
        .filter(x => x);

    if (!isEqual(oldData.generalConsents, generalConsents)) {
        effects.push(api.postUserConsent(generalConsents));
    }

    const rets: [boolean, any, string][] = yield all(effects);
    const errors = rets.filter(x => !x[0]);
    if (errors.length > 0)
        yield put(
            showInlineErrorsOnSubscriptionPreferencesAction(
                errors.reduce(
                    (sum, [, obj, key]) => ((sum[key] = obj), sum),
                    {}
                )
            )
        );
    else yield call(loadConsents, false);

    yield unLockScreenSaga(lockUid);
}

const keys = ["emailconsent", "postconsent", "pushconsent", "smsconsent"];

function isEqual(a: IConsents, b: IConsents) {
    if (!a && !b) return true;
    if (!a || !b) return false;

    for (const key of keys) if (a[key] !== b[key]) return false;

    return true;
}

const isEmpty = (c: IConsents) => keys.every(k => !c[k]);
